import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { size } from "polished";
import React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { VideoButton } from "@brexhq/next/src/components/shared/Chip/VideoButton";
import { Link } from "@brexhq/next/src/components/shared/Link";
import { StyledArrowAsSpanLink } from "@brexhq/next/src/components/shared/Link/ArrowLink";
import type { RebirthLinkProps } from "@brexhq/next/src/components/shared/Link/types";
import { Text } from "@brexhq/shared/components/Typography/Text";
import { Icon } from "@brexhq/shared/components/deprecated/Icons/SingleColor/SingleColorIcon";
import type {
  IBynderImage,
  IBynderVideoWithCaption,
} from "@brexhq/shared/types/BynderAsset";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import type { SpacingValues } from "@brexhq/shared/types/SpacingScale";
import {
  ChipType,
  ImageCoverPosition,
  SanityChipProps,
} from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/Chip";

interface ChipDataType<TChipType extends ChipType, TData> {
  readonly type: TChipType;
  readonly data: TData;
}

/**
 * Navigates to an href when clicked
 */
type PageLinkChip = ChipDataType<
  "Page Link",
  {
    content: string;
    linkLabel: string;
    linkHref: string;
  }
>;

/**
 * Plays a video when clicked
 */
type VideoChip = ChipDataType<
  "Video",
  {
    content: string;
    buttonLabel: string;
    video: IBynderVideoWithCaption;
  }
>;

export type ChipData = PageLinkChip | VideoChip;

export interface ChipProps {
  /**
   * Data specific to the type of chip
   */
  data: ChipData;

  image: Pick<IBynderImage, "datUrl" | "description">;
  /**
   * Which side to place the image on in the chip
   */
  imageSide: ImageCoverPosition;
  /**
   * Optional theme for the chip, defaults to white
   */
  theme?: ModuleThemeType;
  /**
   * Override the the component name in analytics events when Chip is a child
   * of another component
   */
  componentName?: string;
  marginTop: SpacingValues;
}

export const mapSanityPropsToChipProps = (
  sanityChipProps: SanityChipProps,
  theme: ModuleThemeType,
): ChipProps => {
  const finalChipTheme =
    sanityChipProps.chipDesign === "Default (card theme)"
      ? theme
      : sanityChipProps.chipTheme;

  switch (sanityChipProps.chipType) {
    case "Page Link":
      return {
        theme: finalChipTheme,
        marginTop: sanityChipProps.marginTop,
        image: {
          datUrl: sanityChipProps.image?.image?.datUrl ?? "",
          description: sanityChipProps.image?.image?.description ?? "",
        },
        imageSide: sanityChipProps.imageCoverPosition,
        data: {
          type: sanityChipProps.chipType,
          data: {
            content: sanityChipProps.content ?? "",
            linkHref: sanityChipProps.url?.to ?? "",
            linkLabel: sanityChipProps.cta ?? "",
          },
        },
      };
    case "Video": {
      return {
        theme: finalChipTheme,
        marginTop: sanityChipProps.marginTop,
        image: {
          datUrl: sanityChipProps.previewImage?.image?.datUrl ?? "",
          description: sanityChipProps.previewImage?.image?.description ?? "",
        },
        imageSide: sanityChipProps.imageCoverPosition,
        data: {
          type: sanityChipProps.chipType,
          data: {
            buttonLabel: sanityChipProps.cta ?? "",
            content: sanityChipProps.content ?? "",
            video: sanityChipProps.video,
          },
        },
      };
    }
  }
};

const ChipContainer = styled(Container)(({ theme }) => ({
  boxShadow: theme.shadows.flat,
  transition: theme.transitions.cardHover,
  "&:hover": {
    boxShadow: theme.shadows.hover,
    "div[data-name='ArrowWrapper']": {
      transform: "translateX(4px)",
    },
  },
}));

const IconContainer = styled(Container)({
  transform: "translate(-50%, -50%)",
});

const ChipImage = styled.img({
  ...size("100%"),
  objectFit: "cover",
});

const ImageContainer = styled(Container)(({ theme }) => ({
  height: 0,
  minWidth: "33%",
  maxWidth: "33%",
  paddingBottom: "33%",
  overflow: "hidden",
  [theme.breakpoints.SML]: {
    display: "none",
  },
}));

/**
 * Shows an image and text with an optional link.
 */

export const Chip = ({
  image,
  imageSide,
  data,
  componentName = "Chip",
  theme = "PrimaryWhite",
  marginTop,
}: ChipProps) => {
  const { standard } = getModuleThemeColors(theme);
  const { colors } = useTheme();
  const direction = imageSide === "Left" ? "row" : "row-reverse";

  const children = (
    <ChipContainer
      padding="sml"
      gap="sml"
      backgroundColor={standard.background}
      maxWidth="450px"
      width="100%"
      borderRadius="chipRadius"
      margin={[marginTop, 0, 0, 0]}
      direction={{ LRG: direction, MED: direction, SML: "row-reverse" }}
    >
      <ImageContainer borderRadius="chipRadius" position="relative">
        <div
          style={{
            position: "absolute",
            height: "100%",
            width: "100%",
          }}
        >
          <ChipImage src={image.datUrl} alt={image.description ?? ""} />
          {data.type === "Video" && (
            <IconContainer
              position="absolute"
              width={{
                LRG: "40px",
                SML: "32px",
              }}
              height={{
                LRG: "40px",
                SML: "32px",
              }}
              top="50%"
              left="50%"
            >
              <Icon icon="VideoPlay" color={colors.background.primary} />
            </IconContainer>
          )}
        </div>
      </ImageContainer>
      <Container
        direction="column"
        justifyContent="space-between"
        alignItems="flex-start"
        width="100%"
        gap="xsml"
      >
        <Text
          as="p"
          color={standard.textPrimary}
          components={{
            LRG: "Subtitle2",
            MED: "Subtitle2",
            SML: "Subtitle2",
          }}
        >
          {data.data.content}
        </Text>
        <ChipLink
          color={standard.accent}
          componentName={componentName}
          {...data}
        />
      </Container>
    </ChipContainer>
  );

  switch (data.type) {
    case "Page Link":
      return <>{children}</>;
    case "Video":
      return (
        <VideoButton
          section={data.data.buttonLabel}
          topMargin={0}
          componentName={componentName}
          modifier="unstyled"
          label={children}
          video={data.data.video}
        />
      );
  }
};

const ChipLink = (
  props: ChipData & { color: RebirthLinkProps["color"]; componentName: string },
) => {
  switch (props.type) {
    case "Page Link":
      return (
        <Link
          to={props.data.linkHref}
          label={props.data.linkLabel}
          modifier="arrow"
          component={props.componentName}
          color={props.color}
        />
      );
    case "Video":
      return (
        <StyledArrowAsSpanLink>{props.data.buttonLabel}</StyledArrowAsSpanLink>
      );
  }
};
